/* eslint-disable i18next/no-literal-string */
import { CircularProgress, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { GenericDialog } from 'app/components/Alerts/GenericDialog';
import { Cancel } from 'app/components/Common/CancelModal/Cancel';
import { placementDetailsAction } from 'store/redux-store/placement-details/slice';
import { selectPassportCredentials, selectPlacementHeaderData } from 'store/redux-store/placement-details/selectors';
import {
  getExceptionWaiveDetails,
  postExceptionWaiveAttachments,
  postExceptionWaiveDetails,
  updateExceptionWaiveAttachmentTitle,
} from 'app/services/CredentialingServices/CredentialingServices';
import _ from 'lodash';
import { selectUser } from 'oidc/user.selectors';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { placementEventActions } from '../../../Store/PlacementEventDetails.redux';
import {
  DeletedAttachmentsSelection,
  ExceptionWaiveSelection,
  ExceptionWaveIsDeletedSelection,
  ExceptionWaveNewAttachmentSelection,
} from '../../../Store/PlacementEventDetails.selector';
import { IApproverType } from '../DialogComponents/ExceptionDetails/Controls/ApproverType';
import { randomNumber } from '../DialogComponents/ExceptionWaiveAttachments/DocumentDragDropHelper';
import { MarkExceptionContent } from './MarkExceptionContent';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles()((theme: Theme) => ({
  MuiCircularProgressRoot: {
    left: '50%',
    position: 'absolute',
    top: '50vh',
  },
}));
interface IMarkException {
  open: boolean;
  edit: boolean;
  add: boolean;
  credential: any;
  handleClose: () => void;
}

export const MarkExceptionDialog = ({ open, edit, add, credential, handleClose }: IMarkException) => {
  const defaultValues = {
    type: 'Exception',
    approverType: IApproverType.amn,
    expirationDate: null,
    amnApproverName: null,
    amnApproverTitle: null,
    clientApproverName: null,
    clientApproverTitle: null,
    clientDepartment: null,
    notes: null,
  };
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cancel, setCancel] = useState<boolean>(false);
  const passportCredentials = useSelector(selectPassportCredentials);
  const placementDetails = useSelector(selectPlacementHeaderData);
  const exceptionWaiveSel = useSelector(ExceptionWaiveSelection);
  const newAttachments = useSelector(ExceptionWaveNewAttachmentSelection);
  const deletedAttachments = useSelector(DeletedAttachmentsSelection);
  const IsDeletedSelection = useSelector(ExceptionWaveIsDeletedSelection);
  const [selectedStatusType, setSelectedStatusType] = useState({});
  const user = useSelector(selectUser);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formMethods = useForm({
    defaultValues: defaultValues,
  });
  const {
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isDirty, touchedFields},
  } = formMethods;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (IsDeletedSelection) {
      setSubmitDisabled(IsDeletedSelection);
    }
  }, [IsDeletedSelection]);

  const fetchData = async () => {
    if (credential?.exceptionWaiveId) {
      setIsLoading(true);
      var result = await getExceptionWaiveDetails(credential.exceptionWaiveId);
      if (result) {
        dispatch(placementEventActions.setExceptionWaive(formatData(result)));
      }
    }
    setIsLoading(false);
  };

  const formatData = result => {
    if (result && result?.existingAttachments?.length > 0) {
      const updatedAttachments = result?.existingAttachments?.map(item => {
        return { ...item, id: 1000 + randomNumber(99) };
      });

      return { ...result, existingAttachments: updatedAttachments };
    }
    return result;
  };

  const isChanged = (name: string, value: string | null) => {
    setSelectedStatusType({ name, value });
    if (isDirty) {
      if (
        (name !== 'amnApproverName'
          ? getValues('amnApproverName') === exceptionWaiveSel?.amnApproverName ||
            (add ? !getValues('amnApproverName') || getValues('amnApproverName') === '' : false)
          : true) &&
        (name !== 'amnApproverTitle'
          ? getValues('amnApproverTitle') === exceptionWaiveSel?.amnApproverTitle ||
            (add ? !getValues('amnApproverTitle') || getValues('amnApproverTitle') === '' : false)
          : true) &&
        (name !== 'notes'
          ? getValues('notes') === exceptionWaiveSel?.notes ||
            (add ? !getValues('notes') || getValues('notes') === '' : false)
          : true) &&
        (name !== 'clientApproverName'
          ? getValues('clientApproverName') === exceptionWaiveSel?.clientApproverName ||
            (add ? !getValues('clientApproverName') || getValues('clientApproverName') === '' : false)
          : true) &&
        (name !== 'clientApproverTitle'
          ? getValues('clientApproverTitle') === exceptionWaiveSel?.clientApproverTitle ||
            (add ? !getValues('clientApproverTitle') || getValues('clientApproverTitle') === '' : false)
          : true) &&
        (name !== 'clientDepartment'
          ? getValues('clientDepartment') === exceptionWaiveSel?.clientDepartment ||
            (add ? !getValues('clientDepartment') || getValues('clientDepartment') === '' : false)
          : true) &&
        (name !== 'expirationDate'
          ? getValues('expirationDate') === exceptionWaiveSel?.expirationDate ||
            (add ? !getValues('expirationDate') : false)
          : true) &&
        (name !== 'type' ? (!add ? getValues('type') === exceptionWaiveSel?.type : true) : true) &&
        (name === 'attachments' ? true : false) &&
        (exceptionWaiveSel ? value === exceptionWaiveSel[name] : true) &&
        (add ? !value || value === '' : true)
      ) {
        setSubmitDisabled(true);
      } else {
        setSubmitDisabled(false);
      }
    } else {
      setSubmitDisabled(true);
    }
  };

  const validateData = formData => {
    let validData = true;
    if (formData?.amnApproverName && formData.amnApproverName.length > 100) {
      setError('amnApproverName', {
        type: 'validate',
        message: 'character limit: 100',
      });
      validData = false;
    } else {
      clearErrors(['amnApproverName']);
    }
    if (formData?.amnApproverTitle && formData.amnApproverTitle.length > 100) {
      setError('amnApproverTitle', {
        type: 'validate',
        message: 'character limit: 100',
      });
      validData = false;
    } else {
      clearErrors(['amnApproverTitle']);
    }
    if (formData?.clientApproverName && formData.clientApproverName.length > 100) {
      setError('clientApproverName', {
        type: 'validate',
        message: 'character limit: 100',
      });
      validData = false;
    } else {
      clearErrors(['clientApproverName']);
    }
    if (formData?.clientApproverTitle && formData.clientApproverTitle.length > 100) {
      setError('clientApproverTitle', {
        type: 'validate',
        message: 'character limit: 100',
      });
      validData = false;
    } else {
      clearErrors(['clientApproverTitle']);
    }
    if (formData?.clientDepartment && formData.clientDepartment.length > 100) {
      setError('clientDepartment', {
        type: 'validate',
        message: 'character limit: 100',
      });
      validData = false;
    } else {
      clearErrors(['clientDepartment']);
    }
    if (formData?.notes && formData.notes.length > 200) {
      setError('notes', {
        type: 'validate',
        message: 'character limit: 200',
      });
      validData = false;
    } else {
      clearErrors(['notes']);
    }
    if (formData.type === 'Exception' && !formData?.expirationDate) {
      setError('expirationDate', {
        type: 'required',
        message: 'Required',
      });
      validData = false;
    } else {
      clearErrors(['expirationDate']);
    }
    return validData;
  };

  const onSubmit = async (formData, e) => {
    if (!validateData(formData)) return;
    setIsLoading(true);
    if (formData) {
      const saveExceptionWaiveData = {
        exceptionWaiveId: credential?.exceptionWaiveId || 0,
        placementId: placementDetails?.placementId,
        requirementId: credential?.requirementId,
        requirementTypeId: credential?.requirementTypeId !== 0 ? credential?.requirementTypeId : 169,
        typeId: formData.type === 'Exception' ? 1 : 2,
        type: formData.type,
        active: true,
        expirationDate: moment(formData.expirationDate).format('yyyy-MM-DD'),
        notes: formData.notes,
        amnApproverName: formData.amnApproverName || null,
        amnApproverTitle: formData.amnApproverTitle || null,
        clientApproverName: formData.clientApproverName || null,
        clientApproverTitle: formData.clientApproverTitle || null,
        currentUserId: user?.userInfo?.employeeId,
        addedAttachementCount: newAttachments?.length ? newAttachments?.length : 0,
        deletedAttachmentsCount: deletedAttachments?.length ? deletedAttachments?.length : 0,
      };
      const response = await postExceptionWaiveDetails(saveExceptionWaiveData);
      let newExceptionWaiveId = 0;
      if (response && response.status === 200) {
        const updatedCredentials = passportCredentials?.map(item => {
          if (item.id === credential.id) {
            newExceptionWaiveId = response.data.exceptionWaiveId;
            return {
              ...item,
              exceptionWaiveId: response.data.exceptionWaiveId,
              exceptionWaiveType: formData.type,
            };
          }
          return item;
        });
        if (newAttachments) {
          const exceptionWaiveAttachment = new FormData();
          exceptionWaiveAttachment.append(
            'ExceptionWaiveId',
            newExceptionWaiveId !== 0 ? newExceptionWaiveId : credential.exceptionWaiveId,
          );
          newAttachments.map(attachment => {
            exceptionWaiveAttachment.append('Attachments', attachment.data);
          });
          const titles = newAttachments.map(attachment => {
            const item = {
              attachmentUrl: attachment.data.path,
              title: attachment.title,
            };
            return item;
          });
          exceptionWaiveAttachment.append('Title', JSON.stringify(titles));
          exceptionWaiveAttachment.append('CurrentEmployeeId', user.userInfo?.employeeId?.toString() || '0');
          exceptionWaiveAttachment.append(
            'CurrentEmployeeName',
            `${user.userInfo?.firstName} ${user.userInfo?.lastName}`,
          );
          const attachmentResponse = await postExceptionWaiveAttachments(exceptionWaiveAttachment);
          if (attachmentResponse && attachmentResponse.status === 200) {
            // Save successful
          } else {
            dispatch(
              globalActions.setSnackBar({
                message: 'There was an error saving attachments. Please try again',
                severity: 'error',
              }),
            );
          }
        }
        if (exceptionWaiveSel?.existingAttachments && exceptionWaiveSel.existingAttachments.length > 0) {
          const attachmentTitles: any[] = [];
          exceptionWaiveSel.existingAttachments.map(item => {
            const temp = {
              attachmentUrl: item.attachmentUrl,
              title: item.title,
            };
            attachmentTitles.push(temp);
          });
          const exceptionWaiveAttachment = {
            exceptionWaiveId: credential.exceptionWaiveId,
            attachmentTitles: attachmentTitles,
            currentEmployeeId: user.userInfo?.employeeId || 0,
            currentEmployeeName: `${user.userInfo?.firstName} ${user.userInfo?.lastName}`,
          };
          const attachmentResponse = await updateExceptionWaiveAttachmentTitle(exceptionWaiveAttachment);
          if (attachmentResponse && attachmentResponse.status === 200) {
            // Save successful
          } else {
            dispatch(
              globalActions.setSnackBar({
                message: 'There was an error saving attachments. Please try again',
                severity: 'error',
              }),
            );
          }
        }
        dispatch(placementDetailsAction.setPassportCredentials(updatedCredentials || []));
        if (edit) {
          dispatch(
            globalActions.setSnackBar({
              message: 'Exception/Waive data has been updated successfully',
              severity: 'success',
            }),
          );
        } else {
          dispatch(
            globalActions.setSnackBar({
              message: 'Exception/Waive data has been added successfully',
              severity: 'success',
            }),
          );
        }
        setIsLoading(false);
        handleClose();
      } else {
        dispatch(
          globalActions.setSnackBar({
            message: t('common.failedLoad'),
            severity: 'error',
          }),
        );
        setIsLoading(false);
      }
    }
  };

  const onError = (errors, e) => {};

  const handleCancel = () => {
    if (!_.isEmpty(errors) || !_.isEmpty(touchedFields) || !submitDisabled) setCancel(true);
    else yesAction();
  };
  const yesAction = () => {
    handleClose();
    setCancel(false);
  };
  const noAction = () => {
    setCancel(false);
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        {isLoading ? (
          <CircularProgress className={classes.MuiCircularProgressRoot} />
        ) : (
          <>
            <GenericDialog
              open={open}
              disablePortal
              maxWidth="md"
              variant="blue"
              dialogTitleProps={{
                text: `${add ? 'Add Exception / Waive' : edit ? 'Edit Exception / Waive' : 'Exception / Waive'}`,
                closeButton: true,
              }}
              onClose={handleCancel}
              dialogActions={
                add || edit
                  ? [
                      {
                        text: 'Cancel',
                        variant: 'contained',
                        color: 'tertiary',
                        onClick: handleCancel,
                      },
                      {
                        text: edit ? 'Update' : 'Save',
                        variant: 'contained',
                        color: 'primary',
                        type: 'submit',
                        disabled: submitDisabled,
                      },
                    ]
                  : undefined
              }
            >
              <MarkExceptionContent
                edit={edit}
                add={add}
                credentialName={credential?.requirementFullDescription}
                isChanged={isChanged}
                selectedStatusType={selectedStatusType}
              />
            </GenericDialog>
            {cancel && <Cancel openDialog={cancel} handleConfirmAction={yesAction} handleCancelAction={noAction} />}
          </>
        )}
      </form>
    </FormProvider>
  );
};
